import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { 
  Search, 
  Send,
  Phone,
  MessageCircle,
  Plus,
  Paperclip,
  Image,
  FileText,
  Tag,
  Clock,
  User,
  Calendar,
  Trash2
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

import { whatsappUrl } from "../../lib/whatsapp";
const CRMWhatsApp = () => {
  const [conversaciones, setConversaciones] = useState([]);
  const [clientes, setClientes] = useState({});
  const [selectedConv, setSelectedConv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [newConvDialog, setNewConvDialog] = useState(false);
  const [deleteConvId, setDeleteConvId] = useState(null);
  const [newConvData, setNewConvData] = useState({ telefono: "", nombre: "" });
  const [filterTag, setFilterTag] = useState("todos");

  const etiquetas = [
    { value: "nuevo", label: "Nuevo", color: "bg-blue-100 text-blue-800" },
    { value: "pagado", label: "Pagado", color: "bg-emerald-100 text-emerald-800" },
    { value: "en_diseno", label: "En Diseño", color: "bg-purple-100 text-purple-800" },
    { value: "entregado", label: "Entregado", color: "bg-stone-100 text-stone-800" },
    { value: "cotizacion", label: "Cotización", color: "bg-amber-100 text-amber-800" },
    { value: "confirmado", label: "Confirmado", color: "bg-green-100 text-green-800" },
  ];

  const respuestasRapidas = [
    "¡Hola! Gracias por tu interés. ¿En qué puedo ayudarte?",
    "Tu invitación está lista. Te la envío en un momento.",
    "Gracias por tu pago. Comenzamos con el diseño de tu invitación.",
    "¿Me podrías enviar tu comprobante de transferencia?",
    "Perfecto, en 24-48 horas tendrás tu invitación lista.",
  ];

  const fetchData = useCallback(async () => {
    try {
      const [convRes, clientesRes] = await Promise.all([
        axios.get(`${API}/whatsapp/conversaciones`),
        axios.get(`${API}/clientes`)
      ]);
      
      setConversaciones(convRes.data);
      
      const clientesMap = {};
      clientesRes.data.forEach(c => {
        clientesMap[c.cliente_id] = c;
        clientesMap[c.whatsapp] = c;
      });
      setClientes(clientesMap);
    } catch {
      toast.error("Error al cargar conversaciones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSendMessage = async () => {
    if (!mensaje.trim() || !selectedConv) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/whatsapp/conversaciones/${selectedConv.conversacion_id}/mensajes`, {
        contenido: mensaje,
        tipo: "texto"
      });
      
      setMensaje("");
      
      // Refresh conversation
      const response = await axios.get(`${API}/whatsapp/conversaciones/${selectedConv.conversacion_id}`);
      setSelectedConv(response.data);
      await fetchData();
      
      toast.success("Mensaje agregado");
    } catch (error) {
      toast.error("Error al enviar mensaje");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCreateConversation = async () => {
    if (!newConvData.telefono) return;
    
    try {
      const response = await axios.post(`${API}/whatsapp/conversaciones`, newConvData);
      setNewConvDialog(false);
      setNewConvData({ telefono: "", nombre: "" });
      await fetchData();
      setSelectedConv(response.data);
      toast.success("Conversación creada");
    } catch (error) {
      toast.error("Error al crear conversación");
    }
  };

  const handleUpdateEtiquetas = async (convId, etiquetas) => {
    try {
      await axios.put(`${API}/whatsapp/conversaciones/${convId}/etiquetas`, { etiquetas });
      await fetchData();
    } catch (error) {
      toast.error("Error al actualizar etiquetas");
    }
  };

  const toggleEtiqueta = (etiqueta) => {
    if (!selectedConv) return;
    const currentTags = selectedConv.etiquetas || [];
    const newTags = currentTags.includes(etiqueta)
      ? currentTags.filter(t => t !== etiqueta)
      : [...currentTags, etiqueta];
    
    handleUpdateEtiquetas(selectedConv.conversacion_id, newTags);
    setSelectedConv(prev => ({ ...prev, etiquetas: newTags }));
  };

  const filteredConversaciones = conversaciones.filter(conv => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = !q ||
      (conv.nombre || "").toLowerCase().includes(q) ||
      (conv.cliente_nombre || "").toLowerCase().includes(q) ||
      (conv.telefono || "").includes(searchTerm) ||
      (conv.cliente_correo || "").toLowerCase().includes(q) ||
      (conv.cliente_nombre_festejado || "").toLowerCase().includes(q) ||
      (conv.cliente_tipo_evento || "").toLowerCase().includes(q);
    const matchesTag = filterTag === "todos" || (conv.etiquetas || []).includes(filterTag);
    return matchesSearch && matchesTag;
  });

  const handleDeleteConv = async () => {
    if (!deleteConvId) return;
    try {
      await axios.delete(`${API}/whatsapp/conversaciones/${deleteConvId}`);
      toast.success("Conversación eliminada");
      if (selectedConv?.conversacion_id === deleteConvId) setSelectedConv(null);
      await fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    } finally {
      setDeleteConvId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000) {
      return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 604800000) {
      return date.toLocaleDateString('es-MX', { weekday: 'short' });
    }
    return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-display text-3xl text-stone-900">WhatsApp CRM</h1>
          <p className="text-stone-600 mt-1">Gestión de conversaciones con clientes</p>
        </div>
        <Button onClick={() => setNewConvDialog(true)} className="btn-primary" data-testid="btn-nueva-conv">
          <Plus className="w-4 h-4 mr-2" />
          Nueva Conversación
        </Button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Conversations List */}
        <div className="w-96 bg-white border border-stone-200 rounded-sm flex flex-col">
          {/* Search & Filters */}
          <div className="p-4 border-b border-stone-200 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input 
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 border-stone-300 text-sm"
                data-testid="search-conversaciones"
              />
            </div>
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setFilterTag("todos")}
                className={`text-xs px-2 py-1 rounded-sm transition-colors ${
                  filterTag === "todos" ? 'bg-emerald-900 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                Todos
              </button>
              {etiquetas.map(tag => (
                <button
                  key={tag.value}
                  onClick={() => setFilterTag(tag.value)}
                  className={`text-xs px-2 py-1 rounded-sm transition-colors ${
                    filterTag === tag.value ? 'bg-emerald-900 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  {tag.label}
                </button>
              ))}
            </div>
          </div>

          {/* Conversations */}
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-6 h-6 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : filteredConversaciones.length > 0 ? (
              <div>
                {filteredConversaciones.map(conv => {
                  const cliente = clientes[conv.telefono];
                  return (
                    <div
                      key={conv.conversacion_id}
                      onClick={() => setSelectedConv(conv)}
                      className={`p-4 border-b border-stone-100 cursor-pointer hover:bg-stone-50 transition-colors group ${
                        selectedConv?.conversacion_id === conv.conversacion_id ? 'bg-emerald-50 border-l-2 border-l-emerald-900' : ''
                      }`}
                      data-testid={`conv-item-${conv.conversacion_id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-stone-200 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-stone-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-stone-900 truncate">
                              {conv.cliente_nombre || conv.nombre || cliente?.nombre || conv.telefono}
                            </p>
                            <span className="text-xs text-stone-500">
                              {formatDate(conv.ultimo_mensaje_fecha)}
                            </span>
                          </div>
                          {(conv.cliente_tipo_evento || conv.cliente_nombre_festejado) && (
                            <p className="text-xs text-emerald-700 truncate">
                              {conv.cliente_tipo_evento}{conv.cliente_nombre_festejado ? ` · ${conv.cliente_nombre_festejado}` : ""}
                            </p>
                          )}
                          <p className="text-sm text-stone-500 truncate mt-0.5">
                            {conv.ultimo_mensaje || conv.telefono || "Sin mensajes"}
                          </p>
                          {conv.etiquetas?.length > 0 && (
                            <div className="flex gap-1 mt-2">
                              {conv.etiquetas.slice(0, 2).map(tag => {
                                const tagInfo = etiquetas.find(e => e.value === tag);
                                return (
                                  <span key={tag} className={`text-xs px-1.5 py-0.5 rounded-sm ${tagInfo?.color || 'bg-stone-100'}`}>
                                    {tagInfo?.label || tag}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        {conv.no_leidos > 0 && (
                          <span className="w-5 h-5 bg-emerald-900 text-white text-xs rounded-full flex items-center justify-center">
                            {conv.no_leidos}
                          </span>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteConvId(conv.conversacion_id); }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-stone-400 hover:text-red-500"
                          data-testid={`btn-delete-conv-${conv.conversacion_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center text-stone-500">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                <p>No hay conversaciones</p>
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className="flex-1 bg-white border border-stone-200 rounded-sm flex flex-col">
          {selectedConv ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-stone-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-stone-200 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-stone-500" />
                  </div>
                  <div>
                    <p className="font-medium text-stone-900" data-testid="chat-cliente-nombre">
                      {selectedConv.cliente_nombre || selectedConv.nombre || clientes[selectedConv.telefono]?.nombre || selectedConv.telefono}
                    </p>
                    <p className="text-sm text-stone-500">{selectedConv.telefono}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a 
                    href={whatsappUrl(selectedConv.telefono) || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => { if (!whatsappUrl(selectedConv.telefono)) { e.preventDefault(); toast.error("Número de WhatsApp inválido"); } }}
                  >
                    <Button variant="outline" size="sm" className="border-stone-300" data-testid="btn-abrir-whatsapp">
                      <Phone className="w-4 h-4 mr-1" />
                      Abrir WhatsApp
                    </Button>
                  </a>
                  <Button variant="outline" size="sm" onClick={() => setDeleteConvId(selectedConv.conversacion_id)} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="btn-delete-conv-header">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Iter109 · Ficha del cliente asociado */}
              {(selectedConv.cliente_id || selectedConv.cliente_correo || selectedConv.cliente_tipo_evento) && (
                <div className="px-4 py-3 bg-stone-50 border-b border-stone-200 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs" data-testid="chat-cliente-ficha">
                  {selectedConv.cliente_correo && (
                    <div>
                      <p className="text-stone-400 uppercase tracking-wider">Correo</p>
                      <p className="text-stone-800 truncate" title={selectedConv.cliente_correo}>{selectedConv.cliente_correo}</p>
                    </div>
                  )}
                  {selectedConv.cliente_tipo_evento && (
                    <div>
                      <p className="text-stone-400 uppercase tracking-wider">Evento</p>
                      <p className="text-stone-800">{selectedConv.cliente_tipo_evento}</p>
                    </div>
                  )}
                  {selectedConv.cliente_nombre_festejado && (
                    <div>
                      <p className="text-stone-400 uppercase tracking-wider">Festejado</p>
                      <p className="text-stone-800 truncate">{selectedConv.cliente_nombre_festejado}</p>
                    </div>
                  )}
                  {selectedConv.cliente_fecha_evento && (
                    <div>
                      <p className="text-stone-400 uppercase tracking-wider">Fecha</p>
                      <p className="text-stone-800">{selectedConv.cliente_fecha_evento}</p>
                    </div>
                  )}
                  {selectedConv.cliente_id && (
                    <div className="col-span-2 md:col-span-4 flex flex-wrap gap-2 pt-1">
                      <a href={`/admin/clientes/${selectedConv.cliente_id}`} className="text-emerald-800 hover:underline" data-testid="link-expediente-cliente">
                        Ver expediente del cliente →
                      </a>
                      {selectedConv.eventos_cliente?.length > 0 && selectedConv.eventos_cliente.map(ev => (
                        <a key={ev.evento_id} href={`/admin/eventos/${ev.evento_id}`} className="text-blue-700 hover:underline">
                          {ev.nombre || ev.evento_id.slice(-6)} · {ev.fecha}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Tags */}
              <div className="px-4 py-2 border-b border-stone-200 flex items-center gap-2">
                <Tag className="w-4 h-4 text-stone-400" />
                <div className="flex flex-wrap gap-1">
                  {etiquetas.map(tag => (
                    <button
                      key={tag.value}
                      onClick={() => toggleEtiqueta(tag.value)}
                      className={`text-xs px-2 py-1 rounded-sm transition-colors ${
                        (selectedConv.etiquetas || []).includes(tag.value) 
                          ? tag.color 
                          : 'bg-stone-100 text-stone-400 hover:bg-stone-200'
                      }`}
                    >
                      {tag.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                {selectedConv.mensajes?.length > 0 ? (
                  <div className="space-y-4">
                    {selectedConv.mensajes.map((msg) => (
                      <div 
                        key={msg.mensaje_id}
                        className={`flex ${msg.direccion === 'saliente' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`chat-bubble ${msg.direccion === 'saliente' ? 'chat-bubble-outgoing' : 'chat-bubble-incoming'}`}>
                          <p className="text-sm">{msg.contenido}</p>
                          <p className={`text-xs mt-1 ${msg.direccion === 'saliente' ? 'text-white/70' : 'text-stone-400'}`}>
                            {formatDate(msg.fecha)} {msg.enviado_por && `• ${msg.enviado_por}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-stone-500">
                    <div className="text-center">
                      <MessageCircle className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                      <p>No hay mensajes aún</p>
                      <p className="text-sm">Los mensajes se registran aquí para seguimiento</p>
                    </div>
                  </div>
                )}
              </ScrollArea>

              {/* Quick Replies */}
              <div className="px-4 py-2 border-t border-stone-200">
                <p className="text-xs text-stone-500 mb-2">Respuestas rápidas:</p>
                <div className="flex flex-wrap gap-1">
                  {respuestasRapidas.slice(0, 3).map((resp) => (
                    <button
                      key={resp}
                      onClick={() => setMensaje(resp)}
                      className="text-xs px-2 py-1 bg-stone-100 text-stone-600 rounded-sm hover:bg-stone-200 truncate max-w-[200px]"
                    >
                      {resp}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-stone-200">
                <div className="flex gap-2">
                  <Input 
                    placeholder="Escribe un mensaje..."
                    value={mensaje}
                    onChange={(e) => setMensaje(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    className="flex-1 border-stone-300"
                    data-testid="input-mensaje"
                  />
                  <Button 
                    onClick={handleSendMessage}
                    disabled={!mensaje.trim() || sendingMessage}
                    className="btn-primary"
                    data-testid="btn-enviar-mensaje"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-stone-500 mt-2">
                  Nota: Los mensajes se registran para seguimiento. Para enviar por WhatsApp, usa el botón &quot;Abrir WhatsApp&quot;.
                </p>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-stone-500">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 text-stone-300" />
                <p className="font-display text-xl text-stone-900">Selecciona una conversación</p>
                <p className="text-sm mt-2">O crea una nueva para comenzar</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Conversation Dialog */}
      <Dialog open={newConvDialog} onOpenChange={setNewConvDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Nueva Conversación</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="telefono">Teléfono WhatsApp *</Label>
              <Input 
                id="telefono"
                value={newConvData.telefono}
                onChange={(e) => setNewConvData(prev => ({ ...prev, telefono: e.target.value }))}
                placeholder="10 dígitos"
                className="border-stone-300"
                data-testid="input-nuevo-telefono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre (opcional)</Label>
              <Input 
                id="nombre"
                value={newConvData.nombre}
                onChange={(e) => setNewConvData(prev => ({ ...prev, nombre: e.target.value }))}
                placeholder="Nombre del contacto"
                className="border-stone-300"
                data-testid="input-nuevo-nombre"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewConvDialog(false)} className="border-stone-300">
              Cancelar
            </Button>
            <Button onClick={handleCreateConversation} className="btn-primary" data-testid="btn-crear-conv">
              Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteConvId} onOpenChange={() => setDeleteConvId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar conversación?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará el historial de esta conversación.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConv} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CRMWhatsApp;
